import { Suspense, useState } from 'react';
import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { BottomNav } from './BottomNav.js';
import { OfflineBanner } from './OfflineBanner.js';
import { Tour, guidaGiaVista, segnaGuidaVista } from '../features/onboarding/Tour.js';
import { TripEventToast, useTripEvents } from '../lib/tripEvents.js';
import { useCurrentTrip } from '../lib/currentTrip.js';

/**
 * Guscio dell'app.
 *
 * Landmark espliciti (header/nav/main) e skip link come primo elemento
 * focusabile: la navigazione da tastiera e da screen reader non deve attraversare
 * la bottom nav per arrivare al contenuto.
 */
export function AppShell(): ReactNode {
  // La guida al primo accesso. Lo stato si legge una volta sola all'avvio: se
  // rileggesse a ogni render, chiuderla la farebbe riapparire.
  const [guidaAperta, setGuidaAperta] = useState(() => !guidaGiaVista());

  // Modifiche degli altri membri: aggiornano le schermate aperte e compaiono
  // come avviso, così nessuno resta a guardare un itinerario superato.
  const { trip } = useCurrentTrip();
  const ultimaModifica = useTripEvents(trip?.id ?? null);

  return (
    <div className="min-h-dvh bg-bg">
      <a href="#contenuto" className="skip-link">
        Vai al contenuto
      </a>

      <OfflineBanner />
      <TripEventToast modifica={ultimaModifica} />

      <main
        id="contenuto"
        tabIndex={-1}
        className="mx-auto w-full max-w-[var(--content-max-width)] px-4 pb-[calc(var(--bottom-nav-height)+env(safe-area-inset-bottom,0px)+16px)] pt-4"
      >
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>

      <BottomNav />

      {guidaAperta ? (
        <Tour
          onChiudi={() => {
            // Chiudere vale come "vista": una guida che riappare a ogni avvio
            // smette di essere un aiuto e diventa un ostacolo.
            segnaGuidaVista();
            setGuidaAperta(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Placeholder di caricamento delle route lazy.
 * `aria-busy` + `role="status"`: il caricamento e' annunciato, non solo animato.
 */
function RouteFallback(): ReactNode {
  return (
    <div role="status" aria-busy="true" className="flex flex-col gap-3 py-8">
      <span className="sr-only">Caricamento in corso</span>
      <div className="h-6 w-40 animate-pulse rounded-md bg-surface-sunken" />
      <div className="h-32 animate-pulse rounded-lg bg-surface-sunken" />
      <div className="h-32 animate-pulse rounded-lg bg-surface-sunken" />
    </div>
  );
}
